import { fromNullable } from 'fp-ts/lib/Option';
/**
 * Curried map on Option types.
 */
export const mapO = (f) => (x) => x.map(f);
/**
 * Curried map on lists.
 */
export const mapL = (f) => (xs) => xs.map(f);
/**
 * Curried map onto es6 Promises.
 */
export const mapP = (f) => (x) => x.then(f);
/**
 * Monadic bind for es6 promises.
 *
 * (They handle it internally, but this just helps keep type signatures clear)
 */
export const bindP = (f) => (x) => x.then(f);
/**
 * Given a collection of objects of the same type, merge them. Duplicate keys
 * will be overridden such that the right-most is favoured.
 */
export const merge = (...xs) => Object.assign({}, ...xs);
/**
 * Given two object create a new (shallow clone) object that combines the
 * properties of both. Duplicate keys will be overridden in preference of `b`.
 */
export const extend = (a, b) => Object.assign({ __proto__: a.__proto__ }, a, b);
/**
 * Bind a function to a specific context, preserving the type of the orginal
 * function.
 */
// tslint:disable-next-line:ban-types
export const bind = (context, f) => f.bind(context);
/**
 * Debounce a lazy execution context such that it won't run until it hasn't
 * been called for `wait` milliseconds.
 */
export const debounce = (f, wait = 100, context) => {
    let timeout;
    return () => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = window.setTimeout(bind(context, f), wait);
    };
};
/**
 * Check is a predicate evaluates to true for any elements of a list.
 */
export const anyTrue = (xs, f) => xs.reduce((p, x) => f(x) || p, false);
/**
 * Check is a predicate evaluates to true for all elements of a list.
 */
export const allTrue = (xs, f) => xs.reduce((p, x) => f(x) && p, true);
/**
 * Given a collection of elements of the same type, combine them into a map of
 * lists of like objects, keyed on the chosen property value.
 */
export const group = (xs, prop) => xs.reduce((m, x) => {
    const key = x[prop];
    const siblings = m.get(key) || [];
    m.set(key, siblings.concat(x));
    return m;
}, new Map());
/**
 * Find the first element in a list that matches a predicate.
 */
export const find = (f) => (xs) => fromNullable(xs.find(f));
/**
 * Extract the value from an Option, throwing an exception if it's None.
 *
 * Can be used inside Promise chains to either get a value or reject.
 */
export const getOrThrow = (message) => (x) => x.getOrElse(() => { throw new Error(message); });
